import { create } from 'zustand'

import { ApiError } from '@/api/client'
import * as authApi from '@/features/auth/api'
import type { CurrentUser } from '@/features/auth/api'
import { can, canAccess, type Action } from '@/features/auth/permissions'
import { clearTokens, getRefreshToken, setTokens } from '@/features/auth/session'
import { queryClient } from '@/lib/queryClient'

/**
 * `loading` is the state the app boots into: there may be a refresh token in storage, and
 * until it has been exchanged we do not yet know whether this is a signed-in user. Routing
 * on `user === null` before that resolves would bounce a returning user to the login
 * screen on every reload.
 */
type Status = 'loading' | 'authenticated' | 'anonymous'

interface AuthState {
  status: Status
  user: CurrentUser | null
  /** Restore a session from the stored refresh token. Called once at startup. */
  bootstrap: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  /**
   * Takes up a token pair issued by something other than the login form.
   *
   * Storefront signup (`POST /marketplace/signup`) returns a live session, because a
   * shopper who has just typed a password to register should not be asked to type it
   * again mid-basket. That leaves the tokens in the caller's hand with no way to become
   * the app's session -- this is that way.
   */
  adoptSession: (tokens: { access_token: string; refresh_token: string }) => Promise<void>
  signOut: () => Promise<void>
  /** Re-read `/auth/me`, e.g. after an admin changes the current user's roles. */
  reload: () => Promise<void>
  can: (resource: string, action: Action) => boolean
  canAccess: (resource: string) => boolean
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,

  bootstrap: async () => {
    if (!getRefreshToken()) {
      set({ status: 'anonymous', user: null })
      return
    }
    try {
      // No access token in memory yet, so this 401s and `apiFetch` silently exchanges the
      // stored refresh token before replaying it. One round trip more than a cookie
      // session would need, and no cookie to protect against CSRF.
      const user = await authApi.getCurrentUser()
      set({ status: 'authenticated', user })
    } catch {
      clearTokens()
      set({ status: 'anonymous', user: null })
    }
  },

  signIn: async (email, password) => {
    await authApi.login(email, password)
    // Whoever was here before this tab was handed over. A session that expired rather
    // than being signed out leaves its rows behind, and the cache is keyed by what was
    // asked for, not by who asked.
    queryClient.clear()
    try {
      const user = await authApi.getCurrentUser()
      set({ status: 'authenticated', user })
    } catch (error) {
      // Credentials were accepted but the profile call failed. Do not leave the app in a
      // half-signed-in state holding a live token.
      clearTokens()
      set({ status: 'anonymous', user: null })
      throw error instanceof ApiError
        ? error
        : new Error('Signed in, but the profile could not be loaded.')
    }
  },

  adoptSession: async (tokens) => {
    setTokens(tokens)
    // Same reasoning as `signIn`: whoever used this tab before did not necessarily sign
    // out, and the cache is keyed by what was asked for rather than by who asked.
    queryClient.clear()
    try {
      const user = await authApi.getCurrentUser()
      set({ status: 'authenticated', user })
    } catch (error) {
      clearTokens()
      set({ status: 'anonymous', user: null })
      throw error instanceof ApiError
        ? error
        : new Error('Registered, but the profile could not be loaded.')
    }
  },

  signOut: async () => {
    await authApi.logout()
    set({ status: 'anonymous', user: null })
    // Cached rows outlive the token otherwise, and the next person to sign in on this
    // tab sees them before their own first fetch lands.
    queryClient.clear()
  },

  reload: async () => {
    const user = await authApi.getCurrentUser()
    set({ user })
  },

  can: (resource, action) => can(get().user?.permissions ?? [], resource, action),
  canAccess: (resource) => canAccess(get().user?.permissions ?? [], resource),
}))
